import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import StopIcon from '@mui/icons-material/Stop';
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Typography,
} from '@mui/material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers-pro';
import { Languages } from '@remote-voice/utilities';
import { useAtom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useKeyupListener } from '@/components/hooks/useKeyupListener';
import HintBalloon from '@/components/molecules/HintBalloon';
import AudioLinearProgress from '@/components/organisms/audioTest/AudioLinearProgress';
import useAudioInputTester from '@/components/organisms/audioTest/useAudioInputTester';

const micSpeechHintEnabledAtom = atomWithStorage<boolean>(
  'MIC_SPEECH_HINT_ENABLED',
  true
);

const boxShadow = '0px 0px 6px rgba(0, 0, 0, 0.2)';
export type MyMessagePosition = 'start' | 'end';
const ChatFooterMicDual = (props: {
  canUseMic: boolean;
  isTemplateMode?: boolean;
  isReadyMic: boolean;
  isSimultaneous: boolean; // 同通
  subUserLanguage: Languages;
  isRecording: boolean;
  isGuestTyping: boolean;
  isMicMode: boolean;
  myMessagePosition: MyMessagePosition;
  isRemoteMode?: boolean;
  onClickTemplateMessage?: () => void;
  onChangeMyMessagePosition: (value: MyMessagePosition) => void;
  onChangeToKeyboard: (isGuestTyping?: boolean) => void;
  onChangeRemoteModeDual?: () => void;
  onMicStart: (isGuestRecording?: boolean) => void;
  onMicEnd: () => void;
  onMicCancel: () => void;
  onOpenLanguageDialog: (value: boolean) => void;
}) => {
  const { t } = useTranslation('chat');
  const [hintAnchor, setHintAnchor] = useState<HTMLElement | null>(null);
  const [height, setHeight] = useState(17);
  const [isBoxSwapped, setIsBoxSwapped] = useState(
    props.myMessagePosition === 'end'
  );
  const [isGuestRecording, setIsGuestRecording] = useState<boolean | undefined>(
    props.isGuestTyping
  );

  const { t: tCommon } = useTranslation('common');
  const [micSpeechHintEnabled, setMicSpeechHintEnabled] = useAtom(
    micSpeechHintEnabledAtom
  );
  const keepIsRecording = useRef(false);
  const audioTester = useAudioInputTester();
  const audioStart = audioTester.start;
  const audioStop = audioTester.stop;
  const textRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (textRef.current) {
      const textHeight = textRef.current.scrollHeight;
      setHeight(textHeight);
    }
  }, [props.isSimultaneous]);
  useEffect(() => {
    if (
      props.isMicMode === true &&
      props.isRecording === true &&
      isGuestRecording === undefined
    ) {
      setIsGuestRecording(props.isGuestTyping);
    }
  }, [
    props.isGuestTyping,
    props.isMicMode,
    props.isRecording,
    isGuestRecording,
  ]);

  useEffect(() => {
    if (props.isRecording === false && keepIsRecording.current === true) {
      // 有効から無効に切り替わった場合はヒント表示を終了する
      setMicSpeechHintEnabled(false);
      audioStop();
    }
    keepIsRecording.current = props.isRecording;
    if (props.isRecording) {
      audioStart();
    }
  }, [audioStart, audioStop, props.isRecording, setMicSpeechHintEnabled]);
  // キー操作をリッスン
  const onMicStart = props.onMicStart;
  const onMicEnd = props.onMicEnd;
  useKeyupListener(
    useMemo(() => ['ArrowDown', 'ArrowRight'], []),
    useCallback(() => {
      if (props.isRecording) onMicEnd();
      else onMicStart();
    }, [onMicEnd, onMicStart, props.isRecording])
  );

  const onChangBoxSwapper = (value: boolean) => {
    setIsBoxSwapped(value);
    if (value) {
      props.onChangeMyMessagePosition('end');
    } else {
      props.onChangeMyMessagePosition('start');
    }
  };

  const openLanguageDialog = () => {
    props.onOpenLanguageDialog(true);
  };

  return (
    <>
      {props.isRecording && !props.isRemoteMode && (
        <Box width={1} justifyContent="center" display="flex">
          <Box
            width="calc(50vw - 10px)"
            maxWidth={300}
            bgcolor={'white'}
            mt={2}
          >
            <Typography variant="caption" mb={1}>
              {t('deviceTest.label012')}
            </Typography>
            <AudioLinearProgress valueRef={audioTester.volume} />
          </Box>
        </Box>
      )}
      {props.isRemoteMode ? (
        <Box
          width={1}
          height={`${height + 57}px`}
          borderRadius="28px 28px 0px 0px"
          boxShadow={boxShadow}
          zIndex={2}
          bgcolor={'white'}
          display="flex"
        >
          <Box
            // 丸影
            sx={{
              background: 'white',
              borderRadius: 100,
              position: 'absolute',
              left: '80%',
              transform: 'translate(-50%, -22px)',
              width: '74px',
              height: '74px',
              display: 'flex',
              boxShadow: boxShadow,
              zIndex: 1,
            }}
          />
          <Box
            // 影隠し
            sx={{
              background: 'white',
              position: 'absolute',
              left: '80%',
              transform: 'translate(-50%, 0)',
              zIndex: 2,
              height: '54px',
              width: '100px',
            }}
          />

          {
            <>
              {props.isRecording && (
                <Box width="calc(50vw - 90px)" maxWidth={300} ml={2} mt={2}>
                  <Typography variant="caption" mb={1}>
                    {t('deviceTest.label012')}
                  </Typography>
                  <AudioLinearProgress valueRef={audioTester.volume} />
                </Box>
              )}
              <Fab
                ref={(v) => setHintAnchor(v)}
                onClick={() =>
                  props.isRecording ? props.onMicEnd() : props.onMicStart()
                }
                disabled={
                  props.isReadyMic === false || props.canUseMic === false
                }
                size="large"
                color="primary"
                sx={{
                  position: 'absolute',
                  left: '80%',
                  transform: 'translate(-50%, -16px)',
                  width: '62px',
                  height: '62px',
                  boxShadow: 0,
                  ...(props.isRecording
                    ? {
                        border: 2,
                        background: (t) => t.palette.background.default,
                        borderColor: (t) => t.palette.primary.main,
                        color: (t) => t.palette.primary.main,
                        '&:hover': {
                          backgroundColor: 'white',
                        },
                      }
                    : {}),

                  // 録音時のブルーアニメーション
                  animation:
                    props.isRecording && props.isReadyMic
                      ? 'width-increase 1s ease-in-out infinite'
                      : undefined,
                  '@keyframes width-increase': {
                    '0%': {
                      boxShadow: (t) =>
                        `0px 0px 0px 0px rgb(${t.palette.primary.light},0)`,
                    },
                    '50%': {
                      boxShadow: (t) =>
                        `0px 0px 32px 8px ${t.palette.primary.light}`,
                    },
                    '100%': {
                      boxShadow: (t) =>
                        `0px 0px 0px 0px rgb(${t.palette.primary.light},0)`,
                    },
                  },
                }}
              >
                {props.canUseMic ? (
                  props.isSimultaneous ? (
                    props.isRecording ? (
                      <MicIcon sx={{ fontSize: '32px' }} />
                    ) : (
                      <MicIcon sx={{ fontSize: '32px' }} />
                    )
                  ) : props.isRecording ? (
                    <StopIcon sx={{ fontSize: '32px' }} />
                  ) : (
                    <MicIcon sx={{ fontSize: '32px' }} />
                  )
                ) : (
                  <MicOffIcon sx={{ fontSize: '32px' }} />
                )}
                {props.isReadyMic === false && (
                  <CircularProgress
                    size={38}
                    sx={{
                      position: 'absolute',
                      left: props.isRecording ? '10px' : '12px',
                      top: props.isRecording ? '10px' : '12px',
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                )}
                <Typography
                  // マイクアイコン下のキャプション
                  variant="body2"
                  color="primary"
                  fontWeight="bold"
                  width={'200%'}
                  sx={{
                    position: 'absolute',
                    top: props.isRecording ? '62px' : '64px',
                    textTransform: 'none',
                  }}
                  ref={textRef}
                >
                  {props.isSimultaneous
                    ? props.isRecording
                      ? t('stopSimultaneousCaption')
                      : t('startSimultaneousCaption')
                    : props.isRecording
                    ? t('stopConsecutiveCaption')
                    : t('startConsecutiveCaption')}
                </Typography>
              </Fab>
            </>
          }
        </Box>
      ) : (
        <Box
          width="100%"
          display="flex"
          flexDirection="row"
          position="relative"
          sx={{
            overflow: 'hidden',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          {(!props.isRecording ||
            (props.isRecording && isGuestRecording === true)) && (
            <Box
              width={props.isRecording ? '100%' : '50%'}
              height={`${height + 57}px`}
              borderRadius={
                isBoxSwapped ? '0px 0px 0px 0px' : '0px 0px 0px 0px'
              }
              boxShadow={boxShadow}
              zIndex={2}
              bgcolor={(theme) => {
                if (props.isRecording) {
                  return isGuestRecording
                    ? theme.palette.orange.main
                    : theme.palette.primary.main;
                }
                return isBoxSwapped
                  ? theme.palette.orange.main
                  : theme.palette.primary.main;
              }}
              display="flex"
              position="relative"
              left={0}
            >
              <Box
                sx={{
                  width: props.isRecording ? '50%' : '100%',
                  position: 'relative',
                  left: props.isRecording && !isBoxSwapped ? '50%' : 0,
                }}
              >
                <Fab
                  ref={(v) => setHintAnchor(v)}
                  onClick={() => {
                    {
                      props.isRecording
                        ? props.onMicEnd()
                        : props.onMicStart(isBoxSwapped);

                      if (!props.isRecording) {
                        setIsGuestRecording(isBoxSwapped);
                      }
                    }
                  }}
                  disabled={
                    props.isReadyMic === false || props.canUseMic === false
                  }
                  size="large"
                  color="primary"
                  sx={{
                    '&:hover': {
                      backgroundColor: isBoxSwapped
                        ? (theme) => theme.palette.orange.dark
                        : (theme) => theme.palette.primary.dark,
                    },
                    position: 'absolute',
                    top: '50%',
                    ...(props.isRecording && isGuestRecording && !isBoxSwapped
                      ? {
                          left: '15%',
                        }
                      : {
                          right: '15%',
                        }),
                    transform: 'translateY(-50%)',
                    width: '62px',
                    height: '62px',
                    borderRadius: '8px',
                    boxShadow: 0,
                    backgroundColor: isBoxSwapped
                      ? (theme) => theme.palette.orange.main
                      : (theme) => theme.palette.primary.main,
                    color: 'white',
                    border: '2px solid white',
                    ...(props.isRecording
                      ? {
                          border: '2px solid white',
                          background: (theme) => theme.palette.orange.main,
                          borderColor: 'white',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: (theme) =>
                              theme.palette.orange.dark,
                          },
                        }
                      : {}),
                    animation:
                      props.isRecording && props.isReadyMic
                        ? 'width-increase 1s ease-in-out infinite'
                        : undefined,
                    '@keyframes width-increase': {
                      '0%': {
                        boxShadow: (t) =>
                          `0px 0px 0px 0px rgb(${t.palette.primary.light},0)`,
                      },
                      '50%': {
                        boxShadow: (t) =>
                          `0px 0px 32px 8px ${t.palette.primary.light}`,
                      },
                      '100%': {
                        boxShadow: (t) =>
                          `0px 0px 0px 0px rgb(${t.palette.primary.light},0)`,
                      },
                    },
                  }}
                >
                  {props.canUseMic ? (
                    props.isSimultaneous ? (
                      props.isRecording ? (
                        <MicIcon sx={{ fontSize: '32px' }} />
                      ) : (
                        <MicIcon sx={{ fontSize: '32px' }} />
                      )
                    ) : props.isRecording ? (
                      <StopIcon sx={{ fontSize: '32px' }} />
                    ) : (
                      <MicIcon sx={{ fontSize: '32px' }} />
                    )
                  ) : (
                    <MicOffIcon sx={{ fontSize: '32px' }} />
                  )}
                  {props.isReadyMic === false && (
                    <CircularProgress
                      size={38}
                      sx={{
                        position: 'absolute',
                        left: props.isRecording ? '10px' : '12px',
                        top: props.isRecording ? '10px' : '12px',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                  )}
                </Fab>
              </Box>

              {props.isRecording ? (
                <Box
                  sx={{
                    width: '50%',
                    position: 'relative',
                    right: !isBoxSwapped ? '50%' : 0,
                  }}
                >
                  <IconButton
                    onClick={() => props.onMicCancel()}
                    size="medium"
                    sx={{
                      position: 'absolute',
                      ...(isGuestRecording && !isBoxSwapped
                        ? {
                            right: '15%',
                          }
                        : {
                            left: '15%',
                          }),
                      top: '25%',
                      width: '30px',
                      height: '30px',
                      color: 'white',
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '30px' }} />
                  </IconButton>
                </Box>
              ) : (
                <>
                  {!isBoxSwapped ? (
                    <IconButton
                      // Menu/Remote toggle button at menu position
                      onClick={() => {
                        if (props.isTemplateMode) {
                          props.onChangeRemoteModeDual?.();
                        } else {
                          props.onClickTemplateMessage?.();
                        }
                      }}
                      size="large"
                      sx={{
                        position: 'absolute',
                        left: '15%',
                        top: '45%',
                        width: '30px',
                        height: '30px',
                        color: 'white',
                      }}
                    >
                      {props.isTemplateMode ? (
                        <LockOpenIcon sx={{ fontSize: '30px' }} />
                      ) : (
                        <MenuBookIcon sx={{ fontSize: '30px' }} />
                      )}
                    </IconButton>
                  ) : (
                    <>
                      <Box display="flex" alignItems="center">
                        <IconButton
                          onClick={() => openLanguageDialog()}
                          size="large"
                          sx={{
                            position: 'absolute',
                            left: '15%',
                            top: '45%',
                            width: 'auto',
                            height: 'auto',
                            color: 'white',
                            px: 0,
                            py: 0,
                          }}
                        >
                          <Typography
                            variant="button"
                            sx={{
                              textTransform: 'none',
                              fontSize: '14px',
                              mr: 0.5,
                            }}
                          >
                            {tCommon(
                              `localeLanguages.${props.subUserLanguage}`
                            )}
                          </Typography>
                          <ArrowDropDownIcon sx={{ fontSize: '30px' }} />
                        </IconButton>
                      </Box>
                    </>
                  )}

                  <IconButton
                    // 文字入力への切り替えボタン
                    onClick={() => {
                      props.onChangeToKeyboard(isBoxSwapped);
                      setIsGuestRecording(undefined);
                    }}
                    disabled={props.isReadyMic === false}
                    size="large"
                    sx={{
                      position: 'absolute',
                      left: '15%',
                      top: '10%',
                      width: '30px',
                      height: '30px',
                      color: 'white',
                    }}
                  >
                    <KeyboardIcon sx={{ fontSize: '30px' }} />
                  </IconButton>
                </>
              )}
              {props.isRecording && (
                <HintBalloon
                  anchorEl={micSpeechHintEnabled ? hintAnchor : null}
                  caption={t('micSpeechHintMessage')}
                  anchorPosition="CenterTop"
                  onClose={() => setMicSpeechHintEnabled(false)}
                  icon={<RecordVoiceOverIcon />}
                />
              )}
            </Box>
          )}

          {(!props.isRecording ||
            (props.isRecording && isGuestRecording === false)) && (
            <Box
              width={props.isRecording ? '100%' : '50%'}
              height={`${height + 57}px`}
              borderRadius={
                isBoxSwapped ? '0px 0px 0px 0px' : '0px 0px 0px 0px'
              }
              boxShadow={boxShadow}
              zIndex={2}
              bgcolor={(theme) => {
                if (props.isRecording) {
                  return isGuestRecording
                    ? theme.palette.orange.main
                    : theme.palette.primary.main;
                }
                return isBoxSwapped
                  ? theme.palette.primary.main
                  : theme.palette.orange.main;
              }}
              display="flex"
              position="relative"
              left={0}
            >
              <Box
                sx={{
                  width: props.isRecording ? '50%' : '100%',
                  position: 'relative',
                  left: props.isRecording && isBoxSwapped ? '50%' : 0,
                }}
              >
                <Fab
                  ref={(v) => setHintAnchor(v)}
                  onClick={() => {
                    props.isRecording
                      ? props.onMicEnd()
                      : props.onMicStart(!isBoxSwapped);

                    if (!props.isRecording) {
                      setIsGuestRecording(!isBoxSwapped);
                    }
                  }}
                  disabled={
                    props.isReadyMic === false || props.canUseMic === false
                  }
                  size="large"
                  color="primary"
                  sx={{
                    '&:hover': {
                      backgroundColor: isBoxSwapped
                        ? (theme) => theme.palette.primary.dark
                        : (theme) => theme.palette.orange.dark,
                    },
                    position: 'absolute',
                    top: '50%',
                    ...(props.isRecording && !isGuestRecording && !isBoxSwapped
                      ? {
                          right: '15%',
                        }
                      : {
                          left: '15%',
                        }),
                    transform: 'translateY(-50%)',
                    width: '62px',
                    height: '62px',
                    borderRadius: '8px',
                    boxShadow: 0,
                    backgroundColor: isBoxSwapped
                      ? (theme) => theme.palette.primary.main
                      : (theme) => theme.palette.orange.main,
                    color: 'white',
                    border: '2px solid white',
                    ...(props.isRecording
                      ? {
                          border: '2px solid white',
                          background: (theme) => theme.palette.primary.main,
                          borderColor: 'white',
                          color: 'white',
                          '&:hover': {
                            backgroundColor: (theme) =>
                              theme.palette.primary.dark,
                          },
                        }
                      : {}),
                    animation:
                      props.isRecording && props.isReadyMic
                        ? 'width-increase 1s ease-in-out infinite'
                        : undefined,
                    '@keyframes width-increase': {
                      '0%': {
                        boxShadow: (t) =>
                          `0px 0px 0px 0px rgb(${t.palette.primary.light},0)`,
                      },
                      '50%': {
                        boxShadow: (t) =>
                          `0px 0px 32px 8px ${t.palette.primary.light}`,
                      },
                      '100%': {
                        boxShadow: (t) =>
                          `0px 0px 0px 0px rgb(${t.palette.primary.light},0)`,
                      },
                    },
                  }}
                >
                  {props.canUseMic ? (
                    props.isSimultaneous ? (
                      props.isRecording ? (
                        <MicIcon sx={{ fontSize: '32px' }} />
                      ) : (
                        <MicIcon sx={{ fontSize: '32px' }} />
                      )
                    ) : props.isRecording ? (
                      <StopIcon sx={{ fontSize: '32px' }} />
                    ) : (
                      <MicIcon sx={{ fontSize: '32px' }} />
                    )
                  ) : (
                    <MicOffIcon sx={{ fontSize: '32px' }} />
                  )}
                  {props.isReadyMic === false && (
                    <CircularProgress
                      size={38}
                      sx={{
                        position: 'absolute',
                        left: props.isRecording ? '10px' : '12px',
                        top: props.isRecording ? '10px' : '12px',
                        transform: 'translate(-50%, -50%)',
                      }}
                    />
                  )}
                </Fab>
              </Box>
              {props.isRecording ? (
                <Box
                  sx={{
                    width: '50%',
                    position: 'relative',
                    right: !isBoxSwapped ? 0 : '50%',
                  }}
                >
                  <IconButton
                    onClick={() => props.onMicCancel()}
                    size="medium"
                    sx={{
                      position: 'absolute',
                      // right: '25%',
                      ...(!isGuestRecording && !isBoxSwapped
                        ? {
                            left: '15%',
                          }
                        : {
                            right: '15%',
                          }),
                      top: '25%',
                      width: '30px',
                      height: '30px',
                      color: 'white',
                    }}
                  >
                    <CloseIcon sx={{ fontSize: '30px' }} />
                  </IconButton>
                </Box>
              ) : (
                <>
                  {isBoxSwapped ? (
                    <IconButton
                      // Menu/Remote toggle button at menu position (right side)
                      onClick={() => {
                        if (props.isTemplateMode) {
                          props.onChangeRemoteModeDual?.();
                        } else {
                          props.onClickTemplateMessage?.();
                        }
                      }}
                      size="large"
                      sx={{
                        position: 'absolute',
                        right: '15%',
                        top: '45%',
                        width: '30px',
                        height: '30px',
                        color: 'white',
                      }}
                    >
                      {props.isTemplateMode ? (
                        <LockOpenIcon sx={{ fontSize: '30px' }} />
                      ) : (
                        <MenuBookIcon sx={{ fontSize: '30px' }} />
                      )}
                    </IconButton>
                  ) : (
                    <>
                      <Box display="flex" alignItems="center">
                        <IconButton
                          onClick={() => openLanguageDialog()}
                          size="large"
                          sx={{
                            position: 'absolute',
                            right: '15%',
                            top: '45%',
                            width: 'auto',
                            height: 'auto',
                            color: 'white',
                            px: 0,
                            py: 0,
                          }}
                        >
                          <Typography
                            variant="button"
                            sx={{
                              textTransform: 'none',
                              fontSize: '14px',
                              mr: 0.5,
                            }}
                          >
                            {tCommon(
                              `localeLanguages.${props.subUserLanguage}`
                            )}
                          </Typography>
                          <ArrowDropDownIcon sx={{ fontSize: '30px' }} />
                        </IconButton>
                      </Box>
                    </>
                  )}

                  <IconButton
                    // 文字入力への切り替えボタン
                    onClick={() => {
                      props.onChangeToKeyboard(!isBoxSwapped);
                      setIsGuestRecording(undefined);
                    }}
                    disabled={props.isReadyMic === false}
                    size="large"
                    sx={{
                      position: 'absolute',
                      right: '15%',
                      top: '15%',
                      width: '30px',
                      height: '30px',
                      color: 'white',
                    }}
                  >
                    <KeyboardIcon sx={{ fontSize: '30px' }} />
                  </IconButton>
                </>
              )}
              {props.isRecording && (
                <HintBalloon
                  anchorEl={micSpeechHintEnabled ? hintAnchor : null}
                  caption={t('micSpeechHintMessage')}
                  anchorPosition="CenterTop"
                  onClose={() => setMicSpeechHintEnabled(false)}
                  icon={<RecordVoiceOverIcon />}
                />
              )}
            </Box>
          )}

          {!props.isRecording && (
            <IconButton
              onClick={() => onChangBoxSwapper(!isBoxSwapped)}
              size="small"
              sx={{
                position: 'absolute',
                left: '50%',
                top: '80%',
                transform: 'translate(-50%, -50%)',
                width: '40px',
                height: '40px',
                zIndex: 3,
                backgroundColor: 'white',
                boxShadow: boxShadow,
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'white',
                },
              }}
            >
              <CompareArrowsIcon sx={{ fontSize: '30px' }} />
            </IconButton>
          )}
        </Box>
      )}
    </>
  );
};

export default ChatFooterMicDual;
